import React, { useState } from 'react';
import { Box, Chip, Rating, IconButton, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import { marketplaceColors } from '../styles/colors';
import './VehicleCard.css';

const VehicleCard = ({ vehicle, onClick, showPrice = true }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  const images = vehicle.vehicleImageUrls || [];
  const imageUrl = images[currentImageIndex] || vehicle.thumbnailUrl || images[0] || 'https://placehold.co/600x400/cccccc/333333?text=No+Image';
  const hasMultipleImages = images.length > 1;
  const name = `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'Unnamed Car';

  // Get customer rating value - default to 5 if no rating/reviews
  const customerRating = parseFloat(vehicle.rating) || 0;
  const ratingValue = (customerRating === 0 || vehicle.ratingCount === 0) ? 5 : customerRating;

  // Get price - use display price if available (with commission), otherwise base price
  const pricePerDay = vehicle.displayPricePerDayAED || vehicle.pricePerDayAED || 0;

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(vehicle);
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    const timeout = setTimeout(() => setIsHovered(true), 200);
    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    setIsHovered(false);
    setCurrentImageIndex(0);
  };

  return (
    <Box
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: 3,
        height: 360,
        width: '100%',
        cursor: 'pointer',
        transition: 'transform 0.3s ease, boxShadow 0.3s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 6,
        },
        color: 'inherit',
      }}
    >
      {/* Background Image */}
      <Box
        component="img"
        src={imageUrl}
        alt={name}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          zIndex: 1,
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'https://placehold.co/600x400/cccccc/333333?text=No+Image';
        }}
      />

      {/* Navigation Arrows - Only visible on hover */}
      {hasMultipleImages && isHovered && (
        <>
          <IconButton
            onClick={handlePrevImage}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              left: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              size: 'small',
              zIndex: 10,
              opacity: isHovered ? 1 : 0,
              transition: 'all 0.3s ease',
              pointerEvents: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.8)',
                transform: 'translateY(-50%) scale(1.1)',
              },
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            onClick={handleNextImage}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              size: 'small',
              zIndex: 10,
              opacity: isHovered ? 1 : 0,
              transition: 'all 0.3s ease',
              pointerEvents: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.8)',
                transform: 'translateY(-50%) scale(1.1)',
              },
            }}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </>
      )}

      {/* Image Counter - Only visible on hover */}
      {hasMultipleImages && isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 44,
            right: 12,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontSize: '0.75rem',
            fontWeight: 600,
            zIndex: 3,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        >
          {currentImageIndex + 1} / {images.length}
        </Box>
      )}

      {/* Image Dots Indicator - Only visible on hover */}
      {hasMultipleImages && isHovered && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 0.5,
            zIndex: 3,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                setCurrentImageIndex(index);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: currentImageIndex === index ? marketplaceColors.gold.primary : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: currentImageIndex === index ? marketplaceColors.gold.primary : 'rgba(255,255,255,0.8)',
                  transform: 'scale(1.2)',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Content Overlay with Gradient */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 2,
          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0.05) 70%)',
          color: 'white',
          zIndex: 2,
        }}
      >
        {/* Top Section - Specs Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {vehicle.seatingCapacity && (
            <Chip
              icon={<PeopleIcon sx={{ color: 'white!important', fontSize: '14px!important' }} />}
              label={`${vehicle.seatingCapacity}`}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
                fontSize: 11,
              }}
            />
          )}
          {vehicle.fuelType && (
            <Chip
              icon={<LocalGasStationIcon sx={{ color: 'white!important', fontSize: '14px!important' }} />}
              label={vehicle.fuelType}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
                fontSize: 11,
              }}
            />
          )}
          {vehicle.transmission && (
            <Chip
              icon={<SettingsIcon sx={{ color: 'white!important', fontSize: '14px!important' }} />}
              label={vehicle.transmission}
              size="small"
              sx={{
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '& .MuiChip-icon': { color: 'white' },
                fontSize: 11,
              }}
            />
          )}
        </Box>

        {/* Bottom Section - Name, Rating, Price */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            {name}
          </Typography>

          {vehicle.bodyType && (
            <Typography variant="body2" sx={{ mb: 0.5, opacity: 0.8 }}>
              {vehicle.bodyType}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Rating
              value={ratingValue}
              precision={0.5}
              readOnly
              size="small"
              sx={{ color: marketplaceColors.gold.primary }}
            />
            <Typography variant="caption" sx={{ opacity: 0.8, ml: 0.5 }}>
              {ratingValue.toFixed(1)} Customer Rating{vehicle.ratingCount > 0 && ` (${vehicle.ratingCount})`}
            </Typography>
          </Box>

          {showPrice && (
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              AED {parseFloat(pricePerDay).toFixed(2)}
              <Typography component="span" variant="body2" sx={{ opacity: 0.8 }}>
                /day
              </Typography>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VehicleCard;
